import { Button, FormControlLabel, FormLabel, Radio, RadioGroup, TextField } from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { makeStyles } from '@material-ui/core/styles';
import Spin from 'components/Spin';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useObservable, useRetryableObservable } from 'react-use-observable';
import { EMPTY } from 'rxjs';
import { tap } from 'rxjs/operators';
import Error from 'v2/components/Shared/Error';
import { EPaymentStatus } from 'v2/interfaces/shopping';
import { dateService } from 'v2/services/date';
import shoppingService from 'v2/services/shopping/subscription';
import subscriptionsService from 'v2/services/subscriptions';

import type { ICancelReason } from 'v2/interfaces/subscriptions';

const useStyle = makeStyles({
  center: { textAlign: 'center' },
  label: { fontWeight: 600 },
});

export enum JustifyType {
  SUSPEND = 'suspender',
  CANCEL = 'cancelar',
  REACTIVE = 'reativar',
}

type JustifyModalProps = {
  open: boolean;
  type: JustifyType;
  onClose: () => void;
};

const OTHER_REASON_ID = 1;

const JustifyModal = ({ open, type, onClose }: JustifyModalProps) => {
  const [shopping] = useObservable(() => shoppingService.getCurrent(), []);
  const [sending, setSending] = useState<boolean>(false);
  const [refundJustifyId, setRefundJustifyId] = useState<number | undefined>(undefined);
  const [refundOther, setRefundOther] = useState<string>(null);
  const [cancelReasons, cancelReasonsError, , retryCancelReasons] = useRetryableObservable(
    () => subscriptionsService.getSubscriptionCancelReasons(),
    []
  );
  const [removeAccessDate] = useObservable(() => {
    if (!shopping?.id) return EMPTY;
    return subscriptionsService.getRemoveAccessDate(shopping.id);
  }, [shopping]);
  const classes = useStyle();

  useEffect(() => {
    if (!open) {
      setRefundJustifyId(undefined);
      setRefundOther('');
    }
  }, [open]);

  const handleRefundJustify = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setRefundJustifyId(Number(e.target.value));
  }, []);

  const handleRefundOther = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setRefundOther(e.target.value);
  }, []);

  const suspendSubscription = useCallback(() => {
    setSending(true);
    const reasonObj = { reason_id: refundJustifyId };

    if (refundJustifyId === OTHER_REASON_ID && !!refundOther) {
      reasonObj['reason_description'] = refundOther;
    }

    shoppingService
      .suspendSubscription(shopping, reasonObj)
      .pipe(
        tap(() =>
          shoppingService.setCurrent({ ...shopping, ...{ payment_status: { id: EPaymentStatus.SUSPENSO, name: 'Suspenso', alias: 'Suspenso' } } })
        ),
        tap(() =>
          shoppingService.updateSubscriptionInList({
            ...shopping,
            ...{ payment_status: { id: EPaymentStatus.SUSPENSO, name: 'Suspenso', alias: 'Suspenso' } },
          })
        )
      )
      .subscribe(() => {
        setSending(false);
        toast.success('Pedido de suspensão enviado com sucesso.');
        onClose();
      }, toast.error);
  }, [onClose, refundJustifyId, refundOther, shopping]);

  const cancelSubscription = useCallback(() => {
    setSending(true);
    const reasonObj = { reason_id: refundJustifyId };

    if (refundJustifyId === OTHER_REASON_ID && !!refundOther) {
      reasonObj['reason_description'] = refundOther;
    }

    shoppingService
      .cancelSubscription(shopping, reasonObj)
      .pipe(
        tap(() =>
          shoppingService.setCurrent({ ...shopping, ...{ payment_status: { id: EPaymentStatus.CANCELADO, name: 'Cancelado', alias: 'Cancelado' } } })
        ),
        tap(() =>
          shoppingService.updateSubscriptionInList({
            ...shopping,
            ...{ payment_status: { id: EPaymentStatus.CANCELADO, name: 'Cancelado', alias: 'Cancelado' } },
          })
        )
      )
      .subscribe(() => {
        setSending(false);
        toast.success('Pedido de cancelamento enviado com sucesso.');
        onClose();
      }, toast.error);
  }, [onClose, refundJustifyId, refundOther, shopping]);

  const reactivateSubscription = useCallback(() => {
    setSending(true);
    shoppingService
      .reactivateSubscription(shopping)
      .pipe(
        tap(() => shoppingService.setCurrent({ ...shopping, ...{ payment_status: { id: EPaymentStatus.EM_DIA, name: 'Em Dia', alias: 'Em Dia' } } })),
        tap(() =>
          shoppingService.updateSubscriptionInList({
            ...shopping,
            ...{ payment_status: { id: EPaymentStatus.EM_DIA, name: 'Em Dia', alias: 'Em Dia' } },
          })
        )
      )
      .subscribe(() => {
        setSending(false);
        toast.success('Pedido de reativação enviado com sucesso.');
        onClose();
      }, toast.error);
  }, [onClose, shopping]);

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby='form-dialog-title' maxWidth='sm' fullWidth>
      <DialogTitle id='form-dialog-title' className={classes.center}>
        Deseja realmente {type} esse contrato?
      </DialogTitle>
      <DialogContent>
        {sending ? (
          <Spin />
        ) : (
          type !== JustifyType.REACTIVE && (
            <div>
              {!!cancelReasonsError && <Error onRetry={retryCancelReasons} />}
              {!cancelReasonsError && !cancelReasons && <Spin />}
              {!!cancelReasons && (
                <>
                  <FormLabel className={classes.label}>
                    {(type === JustifyType.CANCEL || type === JustifyType.SUSPEND) && removeAccessDate
                      ? `Caso confirme ${
                          type === JustifyType.CANCEL ? 'o cancelamento' : 'a suspensão'
                        }, você terá acesso ao conteúdo até ${dateService.dateToString(new Date(removeAccessDate), 'dd/MM/yyyy')}. `
                      : ''}
                    Por gentileza informe o motivo principal:
                  </FormLabel>

                  <RadioGroup
                    aria-label='Por gentileza informe o motivo principal:'
                    name='refund'
                    value={refundJustifyId}
                    onChange={handleRefundJustify}
                    style={{ textAlign: 'left' }}
                  >
                    {cancelReasons?.map((reason: ICancelReason) => (
                      <FormControlLabel key={reason.id} value={`${reason.id}`} control={<Radio />} label={reason.name} />
                    ))}
                    {refundJustifyId === OTHER_REASON_ID && (
                      <TextField
                        multiline={true}
                        fullWidth={true}
                        InputProps={{ rows: 3, disableUnderline: true }}
                        InputLabelProps={{ shrink: true }}
                        placeholder='Informe o motivo'
                        onChange={handleRefundOther}
                        value={refundOther}
                      />
                    )}
                  </RadioGroup>
                </>
              )}
            </div>
          )
        )}
      </DialogContent>
      <DialogActions>
        <Button disabled={sending} color='default' onClick={onClose}>
          Cancelar
        </Button>
        {type !== JustifyType.REACTIVE ? (
          <Button
            variant='contained'
            color='secondary'
            onClick={type === JustifyType.SUSPEND ? suspendSubscription : type === JustifyType.CANCEL ? cancelSubscription : reactivateSubscription}
            disabled={(refundJustifyId === OTHER_REASON_ID && !refundOther) || !refundJustifyId || sending}
          >
            Confirmar
          </Button>
        ) : (
          <Button variant='contained' color='secondary' onClick={reactivateSubscription} disabled={sending}>
            Confirmar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default JustifyModal;
